import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../commponents/layout/adminLayout";
import DeliveryOrderItemList from "../../commponents/deliveryOrder/detail/DeliveryOrderItemList";
import DosStatusBadge from "../../commponents/dos/DosStatusBadge";
import DosActions from "../../commponents/dos/DosActions";
import { getDosById } from "../../api/dosApi";
import { getAuthUser } from "../../api/authApi";
import { isSalesRole } from "../../lib/roleAccess";
import { getApiErrorMessage, getHttpStatus } from "../../lib/httpError";
import { formatDateOnly, formatDateTime } from "../../helpers/dateFormat";
import type { DosDetail as DosDetailData } from "../../type/dos";

const loadErrorMessage = async (error: unknown) => {
  const status = getHttpStatus(error);
  if (status === 403) return "You don't have access to this DOS.";
  if (status === 404) return getApiErrorMessage(error, "DOS not found.");
  return getApiErrorMessage(error, "Failed to load DOS detail.");
};

export default function DosDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Sales tidak punya akses halaman DO -> nomor DO resmi tanpa tautan.
  const canOpenDeliveryOrder = !isSalesRole(getAuthUser()?.role);

  const [dos, setDos] = useState<DosDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  // Dipanggil saat halaman dibuka dan setelah aksi (sukses, 409, atau 404).
  const loadDetail = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setErrorMessage("");
      const response = await getDosById(Number(id));
      setDos(response.data.data ?? response.data);
    } catch (error) {
      setDos(null);
      setErrorMessage(await loadErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-6 text-center text-gray-400">Loading...</div>
      </AdminLayout>
    );
  }

  if (errorMessage || !dos) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-6 text-center">
          <p role="alert" className="mb-4 text-red-600">
            {errorMessage || "DOS not found."}
          </p>
          <button
            onClick={() => navigate("/dos")}
            className="rounded-xl border border-gray-200 px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50">
            Back to DOS List
          </button>
        </div>
      </AdminLayout>
    );
  }

  const isRejected = dos.status?.code === "REJECTED";

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="rounded-3xl bg-white p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Detail DOS</h2>
              <DosStatusBadge status={dos.status} />
            </div>
            <DosActions dos={dos} onChanged={loadDetail} onDeleted={() => navigate("/dos")} />
          </div>

          {isRejected && (
            <div
              role="alert"
              className="mb-4 rounded-2xl border-2 border-red-300 bg-red-50 p-4 text-red-800">
              <p className="font-semibold">Rejection Reason</p>
              <p className="mt-1 whitespace-pre-line">{dos.rejection_reason || "-"}</p>
            </div>
          )}

          <div className="rounded-2xl border border-gray-100 p-5">
            <h3 className="mb-4 font-semibold">Basic Information</h3>

            <div className="grid grid-cols-2 gap-x-10 gap-y-2 text-sm">
              <Row label="DOS No" value={dos.dos_no} />
              <Row label="Sales Name" value={dos.sales?.name ?? "-"} />

              <Row label="Bill To" value={dos.customer?.company_name || dos.customer?.customer_name || "-"} />
              <Row label="PO Number" value={dos.po_number || "-"} />

              <Row label="Attn" value={dos.customer?.customer_name ?? "-"} />
              <Row label="Delivery Date" value={formatDateOnly(dos.delivery_date)} />

              <Row label="Address" value={dos.customer?.address || "-"} />
              <Row label="Due Date" value={formatDateOnly(dos.due_date)} />

              <Row label="Phone" value={dos.customer?.phone || "-"} />
              <Row label="Quotation" value={dos.quotation?.quotation_no ?? "-"} />

              <Row label="Created By" value={dos.created_by?.name ?? "-"} />
              <Row label="Submitted At" value={formatDateTime(dos.submitted_at)} />

              <Row label="Reviewed By" value={dos.reviewed_by?.name ?? "-"} />
              <Row label="Reviewed At" value={formatDateTime(dos.reviewed_at)} />

              {dos.delivery_order && (
                <div className="flex">
                  <span className="w-36 shrink-0 text-gray-500">Delivery Order</span>
                  <span className="mr-2">:</span>
                  {canOpenDeliveryOrder ? (
                    <Link
                      to={`/delivery_order/detail/${dos.delivery_order.id}`}
                      className="font-medium text-blue-600 hover:underline">
                      {dos.delivery_order.delivery_order_no}
                    </Link>
                  ) : (
                    <span className="font-medium text-gray-800">
                      {dos.delivery_order.delivery_order_no}
                    </span>
                  )}
                </div>
              )}
            </div>

            <p className="mt-4 text-xs text-gray-400">
              Create at {formatDateTime(dos.createdAt)} WIB
            </p>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6">
          <DeliveryOrderItemList
            items={dos.items}
            checkedBy={dos.checked_by}
            approvedBy={dos.approved_by ?? ""}
            receivedBy={dos.received_by}
          />
        </div>
      </div>
    </AdminLayout>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex">
      <span className="w-36 shrink-0 text-gray-500">{label}</span>
      <span className="mr-2">:</span>
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );
}
